import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlignSettings, GridLayoutGapSettings, Orientation } from '@progress/kendo-angular-layout';
import { map, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  private readonly breakpointObserver = inject(BreakpointObserver);

  private readonly $isHorizontalFlow = signal(false);

  private horizontalFlowPoints = [
    Breakpoints.Medium,
    Breakpoints.Large,
    Breakpoints.XLarge,
    Breakpoints.TabletLandscape,
    Breakpoints.HandsetLandscape,
    Breakpoints.WebLandscape,
  ];

  private verticalFlowPoints = [
    Breakpoints.XSmall,
    Breakpoints.Small,
    Breakpoints.TabletPortrait,
    Breakpoints.HandsetPortrait,
    Breakpoints.WebPortrait,
  ];

  public constructor() {
    const allPoints = [...this.horizontalFlowPoints, ...this.verticalFlowPoints];
    this.breakpointObserver
      .observe(allPoints)
      .pipe(
        map(state => this.horizontalFlowPoints.some(bp => state.breakpoints[bp])),
        tap(isHorizontal => this.$isHorizontalFlow.set(isHorizontal)),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  public readonly $orientation = computed<Orientation>(() => {
    const isHorizontal = this.$isHorizontalFlow() === true;
    if (isHorizontal) {
      return 'horizontal';
    }
    return 'vertical';
  });

  public get detailCardGridLayoutGap(): Required<GridLayoutGapSettings> {
    return { cols: 10, rows: 10 };
  }

  public get newRecordFormGridLayoutGap(): Required<GridLayoutGapSettings> {
    return { cols: 10, rows: 16 };
  }

  public readonly $sidebarGridLayoutGap = computed(() => {
    const isHorizontal = this.$isHorizontalFlow();
    if (isHorizontal === true) {
      return { cols: 5 };
    }
    return { rows: 5 };
  });

  public readonly listItemGap = 15;

  public get listItemAlign(): AlignSettings {
    return { horizontal: 'start', vertical: 'top' };
  }

  public readonly $sidebarConfig = computed(() => {
    const isHorizontal = this.$isHorizontalFlow();
    if (isHorizontal === true) {
      return {
        horizontal: true,
        mainCol: 1,
        sideCol: 2,
        mainRow: 1,
        sideRow: 1,
      };
    }
    return {
      horizontal: false,
      mainCol: 1,
      sideCol: 1,
      sideRow: 1,
      mainRow: 2,
    };
  });
}
